import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import './Landing.css';

const Landing = () => {
  const [counters, setCounters] = useState({
    users: 0,
    transactions: 0,
    rewards: 0,
    countries: 0,
  });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activePlan, setActivePlan] = useState(1);
  const statsRef = useRef(null);
  const [statsTriggered, setStatsTriggered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !statsTriggered) {
          setStatsTriggered(true);
          const targets = {
            users: 50000,
            transactions: 1250000,
            rewards: 750000,
            countries: 150,
          };
          const duration = 2000;
          const increment = 50;
          Object.keys(targets).forEach((key) => {
            let current = 0;
            const target = targets[key];
            const step = target / (duration / increment);
            const timer = setInterval(() => {
              current += step;
              if (current >= target) {
                current = target;
                clearInterval(timer);
              }
              setCounters((prev) => ({ ...prev, [key]: Math.round(current) }));
            }, increment);
          });
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [statsTriggered]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: 'bi-shield-lock', title: 'Blockchain Security', description: 'Every transaction is sealed on-chain with military-grade encryption, ensuring 100% immutable records and transparent operations.', color: '#00FFB3' },
    { icon: 'bi-wallet2', title: 'Smart Wallet', description: 'Multi-signature wallet with cold storage support. Your assets are protected by layered security protocols and real-time fraud detection.', color: '#00D4FF' },
    { icon: 'bi-layers', title: 'Staking Rewards', description: 'Lock your tokens and earn up to 25% APY. Smart contract-automated rewards are distributed every 24 hours directly to your wallet.', color: '#7B2FFF' },
    { icon: 'bi-diagram-3', title: 'MLM Network', description: 'A 10-level deep referral network that rewards you every time your team grows. Transparent, real-time commission tracking.', color: '#FF00E5' },
    { icon: 'bi-cash-stack', title: 'Passive Income', description: 'Multiple income streams working simultaneously — staking rewards, team commissions, rank bonuses, and global pool distributions.', color: '#00FF88' },
    { icon: 'bi-people', title: 'Referral System', description: 'Share your unique link and earn commissions across 10 levels. Your network earns — you earn. Simple, transparent, powerful.', color: '#FFB800' },
    { icon: 'bi-lightning-charge', title: 'Instant Transactions', description: 'Lightning-fast blockchain processing with sub-second confirmations and minimal gas fees powered by our optimized smart contracts.', color: '#00FFB3' },
    { icon: 'bi-graph-up-arrow', title: 'Multi-Level Rewards', description: 'Earn on every level of your downline. As your network grows deeper, your earning potential compounds exponentially.', color: '#00D4FF' },
    { icon: 'bi-speedometer2', title: 'Live Analytics', description: 'Real-time dashboard with advanced analytics, income projections, team growth charts, and detailed transaction histories.', color: '#7B2FFF' },
  ];

  const steps = [
    {
      number: '01',
      icon: 'bi-person-plus',
      title: 'Create Your Account',
      description: 'Sign up in under 2 minutes. Complete KYC verification and secure your account with 2FA authentication for maximum protection.',
      badge: 'Free to Join',
    },
    {
      number: '02',
      icon: 'bi-arrow-down-circle',
      title: 'Deposit & Stake',
      description: 'Fund your wallet with your preferred cryptocurrency. Choose a staking plan that matches your goals and start earning immediately.',
      badge: 'Min. $50 USDT',
    },
    {
      number: '03',
      icon: 'bi-people',
      title: 'Build Your Network',
      description: 'Share your referral link and grow your team. Earn commissions from 10 levels of your network with transparent on-chain tracking.',
      badge: 'Up to 10 Levels',
    },
    {
      number: '04',
      icon: 'bi-currency-dollar',
      title: 'Earn & Withdraw',
      description: 'Watch your rewards accumulate daily. Withdraw anytime directly to your crypto wallet with zero hidden fees.',
      badge: 'Daily Payouts',
    },
  ];

  const tokenomics = [
    { label: 'Staking Rewards Pool', percentage: 40, color: '#00FFB3', icon: 'bi-layers' },
    { label: 'Team Development', percentage: 20, color: '#00D4FF', icon: 'bi-people' },
    { label: 'Ecosystem Growth', percentage: 15, color: '#7B2FFF', icon: 'bi-graph-up-arrow' },
    { label: 'Liquidity Reserve', percentage: 12, color: '#FF00E5', icon: 'bi-wallet2' },
    { label: 'Community Rewards', percentage: 8, color: '#FFB800', icon: 'bi-trophy' },
    { label: 'Operations', percentage: 5, color: '#00FF88', icon: 'bi-gear' },
  ];

  const plans = [
    {
      name: 'Starter',
      icon: 'bi-star',
      min: '$50',
      max: '$499',
      daily: '0.5%',
      monthly: '15%',
      duration: '30 Days',
      referral: '5 Levels',
      color: '#00FFB3',
      popular: false,
      features: ['Daily Rewards', '5-Level Referral', 'Smart Dashboard', 'Email Support'],
    },
    {
      name: 'Growth',
      icon: 'bi-rocket',
      min: '$500',
      max: '$4,999',
      daily: '0.8%',
      monthly: '24%',
      duration: '60 Days',
      referral: '8 Levels',
      color: '#7B2FFF',
      popular: true,
      features: ['Daily Rewards', '8-Level Referral', 'Advanced Analytics', 'Priority Support', 'Rank Bonuses'],
    },
    {
      name: 'Elite',
      icon: 'bi-gem',
      min: '$5,000',
      max: 'Unlimited',
      daily: '1.2%',
      monthly: '36%',
      duration: '90 Days',
      referral: '10 Levels',
      color: '#FFB800',
      popular: false,
      features: ['Daily Rewards', '10-Level Referral', 'VIP Dashboard', '24/7 VIP Support', 'Global Pool Share', 'Leadership Bonuses'],
    },
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      location: 'Mumbai, India',
      avatar: 'RK',
      avatarBg: '#00FFB3',
      rank: 'Diamond Leader',
      stars: 5,
      text: 'I joined Antipsun 8 months ago and it completely changed my financial life. The staking rewards are consistent and the referral system is the most transparent I\'ve ever seen. My team of 300+ members earns together every day.',
      earned: '$24,500',
      period: 'in 8 months',
    },
    {
      name: 'Sarah Mitchell',
      location: 'Lagos, Nigeria',
      avatar: 'SM',
      avatarBg: '#7B2FFF',
      rank: 'Gold Leader',
      stars: 5,
      text: 'The blockchain technology behind Antipsun gives me confidence that everything is transparent. I can see every transaction on-chain. The daily payouts to my wallet are a game-changer. Highly recommend to everyone!',
      earned: '$11,200',
      period: 'in 5 months',
    },
    {
      name: 'Chen Wei',
      location: 'Shenzhen, China',
      avatar: 'CW',
      avatarBg: '#00D4FF',
      rank: 'Platinum Leader',
      stars: 5,
      text: 'As a crypto investor for 6 years, I\'ve seen many platforms. Antipsun stands out with its smart contract security, real-time analytics, and genuine community support. The team commissions across 10 levels are phenomenal.',
      earned: '$47,800',
      period: 'in 12 months',
    },
    {
      name: 'Aisha Okonkwo',
      location: 'Nairobi, Kenya',
      avatar: 'AO',
      avatarBg: '#FF00E5',
      rank: 'Silver Leader',
      stars: 5,
      text: 'I was skeptical at first, but after seeing my first withdrawal hit my wallet within minutes, I was convinced. The interface is beautiful and easy to use even for beginners. My passive income now covers my rent!',
      earned: '$5,800',
      period: 'in 3 months',
    },
  ];

  const roadmap = [
    { phase: 'Phase 1', title: 'Platform Launch', items: ['Smart Contract Development', 'Security Audit Completion', 'Website & Dashboard Launch', 'Community Building'], status: 'completed' },
    { phase: 'Phase 2', title: 'Network Growth', items: ['Staking v2.0 Implementation', '10-Level Referral System', 'Mobile App Beta Launch', 'Partnerships Program'], status: 'active' },
    { phase: 'Phase 3', title: 'Expansion', items: ['Major Exchange Listings', 'DeFi Protocol Integration', 'Governance Token Launch', 'Advanced Trading Features'], status: 'upcoming' },
    { phase: 'Phase 4', title: 'Global Scale', items: ['50+ Country Expansion', 'Institutional Partnership', 'Cross-Chain Bridge', 'AI-Powered Portfolio Tool'], status: 'upcoming' },
  ];

  const faqs = [
    { question: 'What is Antipsun and how does it work?', answer: 'Antipsun is an innovative blockchain-powered trading ecosystem designed to provide users with staking rewards, MLM network benefits, and sustainable passive income. Users deposit crypto, stake it to earn daily rewards, and build referral networks to earn multi-level commissions.' },
    { question: 'How do I get started on Antipsun?', answer: 'Simply register for a free account, complete the KYC verification process (takes ~2 minutes), fund your wallet with USDT or supported crypto, choose a staking plan, and start earning daily rewards immediately.' },
    { question: 'What are the staking rewards and how are they calculated?', answer: 'Our staking rewards range from 0.5% to 1.2% daily (15%–36% monthly) depending on your investment plan and lock period. Rewards are calculated automatically by our smart contracts and distributed directly to your wallet every 24 hours.' },
    { question: 'Is my investment secure on Antipsun?', answer: 'Yes. We implement bank-grade security: multi-signature wallets, regular third-party smart contract audits, cold storage for majority of funds, 2FA authentication, and all transactions are immutably recorded on the blockchain.' },
    { question: 'How does the 10-level referral system work?', answer: 'When you refer someone and they invest, you earn a commission. When your referral refers someone, you earn on that level too — up to 10 levels deep. Commission rates vary by level: Level 1 earns the most, tapering to Level 10. All commissions are tracked in real-time on your dashboard.' },
    { question: 'When and how can I withdraw my earnings?', answer: 'Withdrawals are processed instantly to your registered crypto wallet with no minimum lock on rewards. Principal withdrawals depend on your staking plan duration. There are no hidden fees — what you see is what you get.' },
  ];

  const trustBadges = [
    { icon: 'bi-shield-check', label: 'Audited by CertiK' },
    { icon: 'bi-patch-check', label: 'KYC Verified' },
    { icon: 'bi-lock', label: 'SSL Secured' },
    { icon: 'bi-file-earmark-text', label: 'Smart Contract Verified' },
    { icon: 'bi-bank', label: 'Bank-Grade Security' },
    { icon: 'bi-globe', label: '150+ Countries' },
  ];

  return (
    <>
      <Navbar />

      {/* ─── Hero Section ─── */}
      <section className="hero-section">
        <div className="hero-bg">
          <div className="blockchain-network"></div>
          <div className="floating-particles">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="particle" style={{
                left: `${(i * 5.3) % 100}%`,
                animationDelay: `${(i * 0.7) % 10}s`,
                animationDuration: `${15 + (i * 1.1) % 10}s`,
              }}></div>
            ))}
          </div>
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
        </div>

        <div className="container">
          <div className="row align-items-center min-vh-100">
            <div className="col-lg-7">
              <div className="hero-content">
                <div className="hero-badge">
                  <i className="bi bi-lightning-charge-fill"></i>
                  <span>Next-Gen Blockchain Ecosystem</span>
                </div>
                <h1 className="hero-title fade-in">
                  ANTI<span className="hero-title-accent">PSUN</span>
                </h1>
                <h2 className="hero-subtitle slide-up">
                  Future of Blockchain<br />
                  <span className="text-glow">Powered Trading</span>
                </h2>
                <p className="hero-description slide-up">
                  <strong>Antipsun is an innovative part of the blockchain trading ecosystem</strong>,
                  designed to provide users with a modern platform for staking, rewards, and network growth.
                  Experience the power of decentralized finance with cutting-edge technology. Earn daily, grow your network, and achieve financial freedom.
                </p>

                <div className="hero-features">
                  <div className="feature-item">
                    <i className="bi bi-shield-check"></i>
                    <span>Blockchain Secured</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-graph-up-arrow"></i>
                    <span>Up to 36% Monthly</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-people"></i>
                    <span>10-Level Referral</span>
                  </div>
                </div>

                <div className="hero-actions">
                  <Link to="/register" className="btn btn-gradient btn-lg hero-btn-primary">
                    Start Earning Now
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                  <Link to="/login" className="btn btn-outline-gradient btn-lg">
                    Login
                    <i className="bi bi-box-arrow-in-right ms-2"></i>
                  </Link>
                </div>

                <div className="hero-trust">
                  <span className="trust-label">Trusted by</span>
                  <span className="trust-number">50,000+</span>
                  <span className="trust-label">users worldwide</span>
                  <div className="trust-avatars">
                    {['A', 'B', 'C', 'D', 'E'].map((l, i) => (
                      <div key={i} className="trust-avatar" style={{ zIndex: 5 - i, marginLeft: i === 0 ? 0 : '-10px' }}>{l}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="hero-visual">
                <div className="hero-card-stack">
                  <div className="hero-stat-card card-1">
                    <div className="hsc-icon"><i className="bi bi-graph-up-arrow"></i></div>
                    <div>
                      <div className="hsc-value">+$1,247</div>
                      <div className="hsc-label">Today's Earnings</div>
                    </div>
                  </div>
                  <div className="hero-stat-card card-2">
                    <div className="hsc-icon hsc-purple"><i className="bi bi-people-fill"></i></div>
                    <div>
                      <div className="hsc-value">342</div>
                      <div className="hsc-label">Team Members</div>
                    </div>
                  </div>
                  <div className="hero-stat-card card-3">
                    <div className="hsc-icon hsc-blue"><i className="bi bi-wallet2"></i></div>
                    <div>
                      <div className="hsc-value">$24,580</div>
                      <div className="hsc-label">Total Balance</div>
                    </div>
                  </div>
                  <div className="crypto-globe">
                    <div className="globe-inner"></div>
                    <div className="orbit orbit-1"></div>
                    <div className="orbit orbit-2"></div>
                    <div className="orbit orbit-3"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-scroll-indicator">
          <div className="scroll-dot"></div>
        </div>
      </section>

      {/* ─── Trust Badges Strip ─── */}
      <section className="trust-strip">
        <div className="trust-strip-track">
          {[...trustBadges, ...trustBadges].map((badge, i) => (
            <div key={i} className="trust-badge-item">
              <i className={`bi ${badge.icon}`}></i>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Statistics Section ─── */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-3 col-md-6">
              <div className="stat-box glass-card">
                <div className="stat-box-icon"><i className="bi bi-people"></i></div>
                <h3 className="counter">{counters.users.toLocaleString()}+</h3>
                <p>Active Users</p>
                <div className="stat-box-glow"></div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-box glass-card">
                <div className="stat-box-icon stat-icon-blue"><i className="bi bi-arrow-left-right"></i></div>
                <h3 className="counter">{counters.transactions.toLocaleString()}+</h3>
                <p>Transactions</p>
                <div className="stat-box-glow glow-blue"></div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-box glass-card">
                <div className="stat-box-icon stat-icon-purple"><i className="bi bi-currency-dollar"></i></div>
                <h3 className="counter">${counters.rewards.toLocaleString()}+</h3>
                <p>Rewards Distributed</p>
                <div className="stat-box-glow glow-purple"></div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-box glass-card">
                <div className="stat-box-icon stat-icon-pink"><i className="bi bi-globe2"></i></div>
                <h3 className="counter">{counters.countries}+</h3>
                <p>Countries</p>
                <div className="stat-box-glow glow-pink"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="how-section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-badge">Simple Process</div>
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Start earning in 4 simple steps — no crypto expertise required</p>
          </div>

          <div className="steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="step-card glass-card">
                <div className="step-number">{step.number}</div>
                <div className="step-icon-wrap">
                  <i className={`bi ${step.icon}`}></i>
                </div>
                <div className="step-badge">{step.badge}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
                {index < steps.length - 1 && <div className="step-connector"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── About Section ─── */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div className="section-header">
                <div className="section-badge">About Us</div>
                <h2 className="section-title">About Antipsun</h2>
                <p className="section-subtitle">Revolutionizing the Future of Digital Finance</p>
              </div>
              <div className="about-content">
                <p>
                  In the rapidly evolving world of blockchain technology and cryptocurrency trading,
                  <strong> Antipsun</strong> stands as a beacon of innovation and opportunity. We've created more than
                  just a platform — we've built an ecosystem where technology meets opportunity.
                </p>
                <p>
                  Our platform leverages the power of decentralization to create a transparent,
                  secure, and profitable environment for all participants. Through smart contracts
                  and advanced blockchain protocols, we ensure that every transaction is secure,
                  every reward is distributed fairly, and every member has equal opportunity to grow.
                </p>
                <div className="about-highlights">
                  <div className="about-highlight-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Third-party audited smart contracts</span>
                  </div>
                  <div className="about-highlight-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Real-time blockchain transaction transparency</span>
                  </div>
                  <div className="about-highlight-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Automated daily reward distribution</span>
                  </div>
                  <div className="about-highlight-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Multi-layered security with cold storage</span>
                  </div>
                </div>
                <div className="about-stats">
                  <div className="stat-item">
                    <i className="bi bi-shield-check"></i>
                    <div>
                      <h4>100%</h4>
                      <span>Secure</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="bi bi-globe"></i>
                    <div>
                      <h4>24/7</h4>
                      <span>Available</span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <i className="bi bi-lightning"></i>
                    <div>
                      <h4>Instant</h4>
                      <span>Transactions</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="about-visual">
                <div className="about-card-main glass-card">
                  <div className="about-card-header">
                    <span className="about-live-dot"></span>
                    <span>Live Network Activity</span>
                  </div>
                  <div className="about-network-grid">
                    {[...Array(9)].map((_, i) => (
                      <div key={i} className={`network-node ${i === 4 ? 'node-center' : ''}`} style={{ animationDelay: `${i * 0.3}s` }}>
                        <div className="node-dot"></div>
                        {i !== 4 && <div className="node-line"></div>}
                      </div>
                    ))}
                  </div>
                  <div className="about-metrics">
                    <div className="metric-item">
                      <div className="metric-bar" style={{ '--bar-width': '87%', '--bar-color': '#00FFB3' }}>
                        <span>Network Uptime</span>
                        <span>99.9%</span>
                      </div>
                      <div className="metric-progress"><div className="metric-fill" style={{ width: '99.9%', background: '#00FFB3' }}></div></div>
                    </div>
                    <div className="metric-item">
                      <div className="metric-bar" style={{ '--bar-color': '#7B2FFF' }}>
                        <span>Rewards Distributed</span>
                        <span>$750K+</span>
                      </div>
                      <div className="metric-progress"><div className="metric-fill" style={{ width: '75%', background: '#7B2FFF' }}></div></div>
                    </div>
                    <div className="metric-item">
                      <div className="metric-bar" style={{ '--bar-color': '#00D4FF' }}>
                        <span>User Satisfaction</span>
                        <span>97.3%</span>
                      </div>
                      <div className="metric-progress"><div className="metric-fill" style={{ width: '97.3%', background: '#00D4FF' }}></div></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Features Section ─── */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-badge">Capabilities</div>
            <h2 className="section-title">Platform Features</h2>
            <p className="section-subtitle">Everything you need for successful crypto earning and network building</p>
          </div>

          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-lg-4 col-md-6">
                <div className="feature-card glass-card" style={{ '--feature-color': feature.color }}>
                  <div className="feature-icon">
                    <i className={`bi ${feature.icon}`}></i>
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                  <div className="feature-corner"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Income Plans Section ─── */}
      <section id="plans" className="plans-section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-badge">Investment Plans</div>
            <h2 className="section-title">Choose Your Plan</h2>
            <p className="section-subtitle">Transparent, flexible plans designed for every investor level</p>
          </div>

          <div className="plans-grid">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`plan-card glass-card ${plan.popular ? 'plan-popular' : ''} ${activePlan === index ? 'plan-active' : ''}`}
                style={{ '--plan-color': plan.color }}
                onClick={() => setActivePlan(index)}
              >
                {plan.popular && <div className="plan-popular-badge">Most Popular</div>}
                <div className="plan-header">
                  <div className="plan-icon"><i className={`bi ${plan.icon}`}></i></div>
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-range">{plan.min} – {plan.max}</div>
                </div>
                <div className="plan-returns">
                  <div className="plan-return-item">
                    <span className="plan-return-value">{plan.daily}</span>
                    <span className="plan-return-label">Daily Return</span>
                  </div>
                  <div className="plan-divider"></div>
                  <div className="plan-return-item">
                    <span className="plan-return-value">{plan.monthly}</span>
                    <span className="plan-return-label">Monthly ROI</span>
                  </div>
                </div>
                <div className="plan-details">
                  <div className="plan-detail-row">
                    <i className="bi bi-clock"></i>
                    <span>Lock Period: <strong>{plan.duration}</strong></span>
                  </div>
                  <div className="plan-detail-row">
                    <i className="bi bi-diagram-3"></i>
                    <span>Referral: <strong>{plan.referral}</strong></span>
                  </div>
                </div>
                <ul className="plan-features-list">
                  {plan.features.map((f, i) => (
                    <li key={i}><i className="bi bi-check-circle-fill"></i>{f}</li>
                  ))}
                </ul>
                <Link to="/register" className="btn plan-btn">
                  Get Started <i className="bi bi-arrow-right ms-1"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Tokenomics Section ─── */}
      <section className="tokenomics-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5">
              <div className="section-header">
                <div className="section-badge">Token Economy</div>
                <h2 className="section-title">Tokenomics</h2>
                <p className="section-subtitle">A transparent and sustainable token distribution designed for long-term growth and community benefit.</p>
              </div>
              <div className="tokenomics-total">
                <div className="total-supply-card glass-card">
                  <i className="bi bi-coin"></i>
                  <div>
                    <div className="total-supply-value">100,000,000</div>
                    <div className="total-supply-label">Total Token Supply</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="tokenomics-bars">
                {tokenomics.map((item, index) => (
                  <div key={index} className="token-bar-item">
                    <div className="token-bar-header">
                      <div className="token-bar-label">
                        <i className={`bi ${item.icon}`} style={{ color: item.color }}></i>
                        <span>{item.label}</span>
                      </div>
                      <span className="token-bar-pct" style={{ color: item.color }}>{item.percentage}%</span>
                    </div>
                    <div className="token-bar-track">
                      <div
                        className="token-bar-fill"
                        style={{
                          '--fill-width': `${item.percentage}%`,
                          '--fill-color': item.color,
                          animationDelay: `${index * 0.2}s`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Testimonials Section ─── */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-badge">Success Stories</div>
            <h2 className="section-title">What Our Members Say</h2>
            <p className="section-subtitle">Real earnings, real people — join 50,000+ successful members worldwide</p>
          </div>

          <div className="testimonials-wrapper">
            <div className="testimonials-track" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
              {testimonials.map((t, index) => (
                <div key={index} className="testimonial-slide">
                  <div className="testimonial-card glass-card">
                    <div className="testimonial-quote">
                      <i className="bi bi-quote"></i>
                    </div>
                    <p className="testimonial-text">{t.text}</p>
                    <div className="testimonial-footer">
                      <div className="testimonial-avatar" style={{ background: t.avatarBg }}>
                        {t.avatar}
                      </div>
                      <div className="testimonial-info">
                        <h5>{t.name}</h5>
                        <span className="testimonial-location"><i className="bi bi-geo-alt"></i> {t.location}</span>
                        <span className="testimonial-rank">{t.rank}</span>
                      </div>
                      <div className="testimonial-earnings">
                        <div className="earned-value">{t.earned}</div>
                        <div className="earned-label">earned {t.period}</div>
                        <div className="testimonial-stars">
                          {[...Array(t.stars)].map((_, i) => <i key={i} className="bi bi-star-fill"></i>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="testimonial-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`testimonial-dot ${activeTestimonial === i ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Testimonial ${i + 1}`}
                ></button>
              ))}
            </div>

            <button className="testimonial-nav testimonial-prev" onClick={() => setActiveTestimonial(p => (p - 1 + testimonials.length) % testimonials.length)}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <button className="testimonial-nav testimonial-next" onClick={() => setActiveTestimonial(p => (p + 1) % testimonials.length)}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </section>

      {/* ─── Roadmap Section ─── */}
      <section id="roadmap" className="roadmap-section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-badge">Our Journey</div>
            <h2 className="section-title">Roadmap</h2>
            <p className="section-subtitle">Our strategic path towards building the future of decentralized finance</p>
          </div>

          <div className="roadmap-timeline">
            {roadmap.map((item, index) => (
              <div key={index} className={`timeline-item ${item.status}`}>
                <div className="timeline-marker">
                  <i className={`bi ${item.status === 'completed' ? 'bi-check-circle-fill' : item.status === 'active' ? 'bi-circle-fill' : 'bi-circle'}`}></i>
                </div>
                <div className="timeline-content glass-card">
                  <div className="timeline-status-badge">{item.status}</div>
                  <h4>{item.phase}</h4>
                  <h5>{item.title}</h5>
                  <ul>
                    {item.items.map((task, i) => (
                      <li key={i}>{task}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ Section ─── */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-badge">Got Questions?</div>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Everything you need to know about Antipsun — answered clearly</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="accordion" id="faqAccordion">
                {faqs.map((faq, index) => (
                  <div key={index} className="accordion-item">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#faq${index}`}
                        aria-expanded={index === 0}
                      >
                        <i className="bi bi-question-circle me-2"></i>
                        {faq.question}
                      </button>
                    </h2>
                    <div
                      id={`faq${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                      data-bs-parent="#faqAccordion"
                    >
                      <div className="accordion-body">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA Section ─── */}
      <section className="cta-section">
        <div className="cta-bg">
          <div className="cta-orb cta-orb-1"></div>
          <div className="cta-orb cta-orb-2"></div>
          <div className="cta-grid"></div>
        </div>
        <div className="container">
          <div className="cta-inner">
            <div className="cta-icon-wrap">
              <i className="bi bi-rocket-takeoff-fill"></i>
            </div>
            <h2 className="cta-title">Ready to Start Earning?</h2>
            <p className="cta-subtitle">
              Join 50,000+ members already earning passive income on Antipsun.
              Registration is free. Your first reward arrives within 24 hours of staking.
            </p>
            <div className="cta-features">
              <span><i className="bi bi-check-circle-fill"></i> Free Registration</span>
              <span><i className="bi bi-check-circle-fill"></i> No Hidden Fees</span>
              <span><i className="bi bi-check-circle-fill"></i> Daily Payouts</span>
              <span><i className="bi bi-check-circle-fill"></i> Instant Withdrawals</span>
            </div>
            <div className="cta-actions">
              <Link to="/register" className="btn btn-gradient btn-lg cta-btn-primary">
                <i className="bi bi-rocket me-2"></i>
                Create Free Account
              </Link>
              <Link to="/login" className="btn btn-outline-gradient btn-lg">
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Sign In
              </Link>
            </div>
            <div className="cta-disclaimer">
              <i className="bi bi-shield-check"></i>
              <span>Secured by blockchain technology. 100% transparent and audited.</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Landing;